import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

import Loader from '@/components/atoms/Loader';
import { SavingBar } from '@/components/atoms/SavingBar';
import { Title } from '@/components/atoms/Title';
import NavigationTabs from '@/components/organisms/RouterNavigationTabs';
import { useAuthCookieHandler } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { routes } from '@/core/router/routes';
import {
    useGetBrandQuery,
    useUpdateBrandOptionsMutation,
    useGetBrandRestaurantsQuery,
    useGetBrandModuleOrderingFlowQuery,
    useUpdateBrandModuleOrderingFlowMutation,
    useGetBrandRestaurantFamiliesQuery,
    useSetBrandRestaurantFamiliesMutation,
} from '@/services/innovorder/brand/brand.endpoints';
import { ImageInput } from '@/components/molecules/ImageInput';
import { Input } from '@/components/atoms/Form/Input';
import { Button } from '@/components/atoms/Button';
import Modal from '@/components/atoms/Modal';
import { SvyIcon } from '@/components/savory/SvyIcon';
import type { BrandRestaurantFamily } from '@/services/innovorder/brand/brand.type';
import {
    useGetRestaurantKioskUsersQuery,
    useGetUserRestaurantsQuery,
    useUpdateUserRestaurantsMutation,
} from '@/services/innovorder/user/user.endpoints';
import { useGetFilesQuery } from '@/services/innovorder/library/library.endpoints';
import { BrandOptionName, BrandOptions } from '@/services/innovorder/brand/brand.type';
import { Switch } from '@/components/atoms/Switch';
import Select from '@/components/atoms/Select/Select';
import { ChannelId } from '@/services/innovorder/channel/channel.type';
import MultiSelect from '@/components/atoms/MultiSelect/MultiSelect';
import { MultiValue } from 'react-select';
import { SelectOptionInterface } from '@/components/atoms/MultiSelect/MultiSelect.interface';
import { GetUserRestaurantsResponse } from '@/services/innovorder/user/user.type';
import {
    TitleContainer,
    Container,
    Item,
    MultiRestaurantsContainer,
    MultiRestaurantsDropdownContainer,
    SelectContainer,
    FamiliesContainer,
    FamilyCard,
    FamilyImage,
    FamilyNameContainer,
    FamilyName,
    DeleteButton,
    EmptyMessage,
    NewFamilyForm,
    SmallImageInput,
    DragHandle,
} from './Options.style';
import { ensureParentRestaurantAssigned, markParentRestaurantAsFixed } from './Options.utils';
import { FormattedMessage, useIntl } from 'react-intl';

interface ParamTypes {
    brandId: string;
}

export const GENERAL_OPTIONS: Partial<Record<BrandOptionName, string>> = {
    [BrandOptionName.EDUCATION_MODE]: 'options.educationMode',
    [BrandOptionName.BIRTHDATE]: 'options.birthdate',
    [BrandOptionName.TRIP_ADVISOR_USERNAME]: 'options.tripAdvisorUsername',
    [BrandOptionName.ADVANCED_SECURITY_RULES]: 'options.advancedSecurityRules',
    [BrandOptionName.OPT_IN]: 'options.optIn',
    [BrandOptionName.PRODUCTION_DECLARATION]: 'options.productionDeclaration',
    [BrandOptionName.GRANTS_AND_ENTRANCE_FEE]: 'options.grantsAndEntranceFee',
    [BrandOptionName.PIN_CODE_DOUBLE_ATHENTICATION]: 'options.pinCodeDoubleAuth',
    [BrandOptionName.CHARLEMAGNE_EXPORT]: 'options.charlemagneExport',
    [BrandOptionName.HANDLE_BADGE_4_CHAR]: 'options.handleBadgeForChar',
    [BrandOptionName.AUTO_BILLING]: 'options.autoBilling',
    [BrandOptionName.LINK_PRODUCT_IMAGES_BY_SKU]: 'options.linkProductImageBySku',
    [BrandOptionName.EXTERNAL_IDS_MAPPING]: 'options.externalIdsMapping',
    [BrandOptionName.LINK_PRODUCT_SKU_TO_EXTERNAL_MAPPING]: 'options.linkProductSkuToExternalMapping',
    [BrandOptionName.LINK_STUDENT_NUMBER_TO_EXTERNAL_MAPPING]: 'options.linkStudentNumberToExternalMapping',
    [BrandOptionName.EWALLET_AUTO_RELOAD]: 'options.ewalletAutoReload',
    [BrandOptionName.ENABLE_ORDER_DETAIL_V2]: 'options.enableOrderDetailV2',
    [BrandOptionName.ENABLE_SODEXO_DICTIONARY]: 'options.enableSodexoDictionary',
    [BrandOptionName.CONFIGURE_SODEXO_CONSUMER_TRANSACTION]: 'options.configureSodexoConsumerTransaction',
    [BrandOptionName.USE_PICKUP_POINTS]: 'options.usePickupPoints',
    [BrandOptionName.ENABLE_LINKING_MENU_TO_POS_AND_WEB]: 'options.enableLinkingMenuToPosAndWeb',
};

export const FOODCOURT_OPTIONS: Partial<Record<BrandOptionName, string>> = {
    [BrandOptionName.FOOD_COURT_MODE]: 'options.foodCourtMode',
    [BrandOptionName.SHUFFLE_RESTAURANTS]: 'options.shuffleRestaurants',
};

export const MULTI_RESTAURANT_OPTIONS: Partial<Record<BrandOptionName, string>> = {
    [BrandOptionName.MULTI_RESTAURANT_MODE]: 'options.multiRestaurantMode',
};

export const Options: React.FunctionComponent<React.PropsWithChildren> = () => {
    const { token } = useAuthCookieHandler();
    const { brandId } = useParams<ParamTypes>();
    const intl = useIntl();

    const [options, setOptions] = useState<BrandOptions>({});
    const [mainRestaurantId, setMainRestaurantId] = useState<number | undefined>(undefined);
    const [mainUserId, setMainUserId] = useState<number | undefined>(undefined);

    const [newFamilyName, setNewFamilyName] = useState('');
    const [newFamilyImageId, setNewFamilyImageId] = useState<number | null>(null);
    const [familyToDelete, setFamilyToDelete] = useState<BrandRestaurantFamily | null>(null);

    const {
        data: brand,
        isLoading,
        refetch: refetchBrand,
    } = useGetBrandQuery({ brandId: parseInt(brandId, 10), token }, { refetchOnMountOrArgChange: true });

    const { data: moduleOrderingFlow, refetch: refetchModuleOrderingFlow } = useGetBrandModuleOrderingFlowQuery(
        { brandId: parseInt(brandId, 10) },
        { refetchOnMountOrArgChange: true },
    );

    const { data: familiesData, isLoading: isLoadingFamilies } = useGetBrandRestaurantFamiliesQuery(
        { brandId: parseInt(brandId, 10), token },
        { refetchOnMountOrArgChange: true },
    );

    const families = useMemo(
        () => (familiesData ? [...familiesData].sort((a, b) => a.position - b.position) : undefined),
        [familiesData],
    );

    const { data: libraryFiles } = useGetFilesQuery(
        { brandId: parseInt(brandId, 10), limit: 1000, offset: 0 },
        { skip: !options?.[BrandOptionName.FOOD_COURT_MODE]?.isActivated },
    );

    const imageUrlMap = useMemo(() => {
        const map = new Map<number, string>();
        libraryFiles?.forEach((file) => {
            map.set(file.imageId, file.fullUrl);
        });
        return map;
    }, [libraryFiles]);

    const { data: assignedRestaurantsData, refetch: refetchUserRestaurants } = useGetUserRestaurantsQuery(
        {
            userId: mainUserId ?? 0,
        },
        { skip: !mainUserId },
    );

    const mapAssignedRestaurantsDataToSelectOptionInterface = (
        restaurants: GetUserRestaurantsResponse,
    ): MultiValue<SelectOptionInterface<number>> => {
        return restaurants.map((restaurant) => ({
            value: restaurant.restaurantId,
            label: restaurant.name,
        }));
    };

    const [userAssignedRestaurants, setUserAssignedRestaurants] = useState<MultiValue<SelectOptionInterface<number>>>(
        [],
    );

    const assignedRestaurants = useMemo(() => {
        const userAssignedRestaurantsChanged =
            userAssignedRestaurants.length !== (assignedRestaurantsData?.length ?? 0) ||
            userAssignedRestaurants.some(
                (restaurant) => !assignedRestaurantsData?.some((r) => r.restaurantId === restaurant.value),
            );

        const list =
            userAssignedRestaurantsChanged && userAssignedRestaurants.length > 0
                ? userAssignedRestaurants
                : mapAssignedRestaurantsDataToSelectOptionInterface(assignedRestaurantsData ?? []);

        // The parent restaurant (restaurant mère) cannot be removed from the user's
        // assigned restaurants — otherwise the user disappears from its own kiosk users
        // page (the listing INNER JOINs on restaurant_user with the queried restaurant).
        return markParentRestaurantAsFixed(list, mainRestaurantId);
    }, [userAssignedRestaurants, assignedRestaurantsData, mainRestaurantId]);

    const tabRoutes = React.useMemo(() => routes.filter(({ tab }) => tab === 'brands'), []);

    const [updateOptions, { isLoading: isLoadingUpdate }] = useUpdateBrandOptionsMutation();
    const changeCount = useMemo(() => {
        const optionChanges = Object.keys({
            ...GENERAL_OPTIONS,
            ...FOODCOURT_OPTIONS,
            ...MULTI_RESTAURANT_OPTIONS,
        }).reduce(
            (count, optionName) =>
                brand?.options?.[optionName as BrandOptionName]?.isActivated !==
                options?.[optionName as BrandOptionName]?.isActivated
                    ? count + 1
                    : count,
            0,
        );

        const mainRestaurantChanged = (moduleOrderingFlow?.mainRestaurantId ?? undefined) !== mainRestaurantId;
        const mainUserChanged = (moduleOrderingFlow?.mainUserId ?? undefined) !== mainUserId;
        const assignedRestaurantsChanged =
            assignedRestaurants.length !== (assignedRestaurantsData?.length ?? 0) ||
            assignedRestaurants.some(
                (restaurant) => !assignedRestaurantsData?.some((r) => r.restaurantId === restaurant.value),
            );

        return (
            optionChanges +
            (mainRestaurantChanged ? 1 : 0) +
            (mainUserChanged ? 1 : 0) +
            (assignedRestaurantsChanged ? 1 : 0)
        );
    }, [
        moduleOrderingFlow?.mainRestaurantId,
        moduleOrderingFlow?.mainUserId,
        mainRestaurantId,
        mainUserId,
        brand?.options,
        options,
        assignedRestaurants,
        assignedRestaurantsData,
    ]);

    const [updateModuleOrderingFlow, { isLoading: isLoadingUpdateModuleOrderingFlow }] =
        useUpdateBrandModuleOrderingFlowMutation();

    const [updateUserRestaurants, { isLoading: isLoadingUpdateUserRestaurants }] = useUpdateUserRestaurantsMutation();

    const [setBrandRestaurantFamilies, { isLoading: isSavingFamilies }] = useSetBrandRestaurantFamiliesMutation();

    const brandKioskRestaurants = useGetBrandRestaurantsQuery({
        brandId: parseInt(brandId, 10),
        active: true,
        channels: [ChannelId.KIOSK],
    });
    const eligibleRestaurants =
        brandKioskRestaurants.data?.map((restaurant) => ({
            value: restaurant.restaurantId,
            label: restaurant.name,
        })) ?? [];

    const { data: kioskUsersData } = useGetRestaurantKioskUsersQuery(
        { brandId: parseInt(brandId, 10), restaurantId: mainRestaurantId ?? 0 },
        { skip: !mainRestaurantId },
    );

    const eligibleUsers = useMemo(() => {
        if (!mainRestaurantId || !kioskUsersData) return [];

        return kioskUsersData.map((user) => ({
            value: user.userId,
            label: `${user.firstName} ${user.lastName} (${user.email})`,
        }));
    }, [mainRestaurantId, kioskUsersData]);

    const assignableRestaurants = useMemo(() => {
        if (!mainRestaurantId || !brandKioskRestaurants.data) return [];

        return mapAssignedRestaurantsDataToSelectOptionInterface(brandKioskRestaurants.data);
    }, [mainRestaurantId, brandKioskRestaurants.data]);

    const handleChange = (optionName: BrandOptionName) => (value: boolean) => {
        const newOptions = { ...options, [optionName]: { ...options?.[optionName], isActivated: value } };
        if (!brand?.options?.[optionName as BrandOptionName] && !value) delete newOptions?.[optionName];
        setOptions(newOptions);
    };

    const handleChangeMainRestaurant = (id: number) => {
        setMainRestaurantId(id);
    };

    const handleChangeMainUser = (id: number) => {
        setMainUserId(id);
        setUserAssignedRestaurants([]);
    };

    const handleChangeAssignedRestaurants = (value: MultiValue<SelectOptionInterface<number>>) => {
        const parentLabel = brandKioskRestaurants.data?.find((r) => r.restaurantId === mainRestaurantId)?.name;
        setUserAssignedRestaurants(ensureParentRestaurantAssigned(value, mainRestaurantId, parentLabel));
    };

    const handleSave = async () => {
        const mainRestaurantChanged = (moduleOrderingFlow?.mainRestaurantId ?? undefined) !== mainRestaurantId;
        const mainUserChanged = (moduleOrderingFlow?.mainUserId ?? undefined) !== mainUserId;
        const assignedRestaurantsChanged =
            assignedRestaurants.length !== (assignedRestaurantsData?.length ?? 0) ||
            assignedRestaurants.some(
                (restaurant) => !assignedRestaurantsData?.some((r) => r.restaurantId === restaurant.value),
            );

        const updatePromises: Promise<unknown>[] = [];
        updatePromises.push(updateOptions({ token, options, brandId: parseInt(brandId, 10) }));

        if (moduleOrderingFlow && (mainRestaurantChanged || mainUserChanged)) {
            updatePromises.push(
                updateModuleOrderingFlow({
                    token,
                    ...moduleOrderingFlow,
                    mainRestaurantId: mainRestaurantChanged
                        ? mainRestaurantId ?? null
                        : moduleOrderingFlow.mainRestaurantId,
                    mainUserId: mainUserChanged ? mainUserId ?? null : moduleOrderingFlow.mainUserId,
                }),
            );
        }

        if (mainUserId && assignedRestaurantsChanged) {
            updatePromises.push(
                updateUserRestaurants({
                    userId: mainUserId,
                    restaurantIds: assignedRestaurants.map((restaurant) => restaurant.value),
                }),
            );
        }

        await Promise.all(updatePromises);

        refetchBrand();
        refetchModuleOrderingFlow();
        refetchUserRestaurants();
    };

    const handleCancel = () => {
        setOptions(brand?.options || {});
        setMainRestaurantId(moduleOrderingFlow?.mainRestaurantId ?? undefined);
        setMainUserId(moduleOrderingFlow?.mainUserId ?? undefined);
        setUserAssignedRestaurants(
            mapAssignedRestaurantsDataToSelectOptionInterface(assignedRestaurantsData ?? []) ?? [],
        );
    };

    useEffect(() => {
        setOptions(brand?.options || {});
        setMainRestaurantId(moduleOrderingFlow?.mainRestaurantId ?? undefined);
        setMainUserId(moduleOrderingFlow?.mainUserId ?? undefined);
    }, [brand, moduleOrderingFlow]);

    const isSwitchDisabled = (optionName: BrandOptionName) => {
        return optionName === BrandOptionName.ENABLE_ORDER_DETAIL_V2 && options?.[optionName]?.isActivated;
    };

    const handleCreateFamily = async () => {
        if (!newFamilyName.trim() || !newFamilyImageId || !families) return;

        const maxPosition = families.reduce((max, f) => Math.max(max, f.position), 0);

        const existingFamilies = families.map((f) => ({
            brandRestaurantFamilyId: f.brandRestaurantFamilyId,
            name: f.name,
            imageId: f.imageId,
            position: f.position,
        }));

        const newFamily = {
            name: newFamilyName.trim(),
            imageId: newFamilyImageId,
            position: maxPosition + 1,
        };

        try {
            await setBrandRestaurantFamilies({
                token,
                brandId: parseInt(brandId, 10),
                brandRestaurantFamilies: [...existingFamilies, newFamily],
            }).unwrap();

            setNewFamilyName('');
            setNewFamilyImageId(null);
        } catch (error) {
            console.error('Error creating family:', error);
        }
    };

    const canCreateFamily = newFamilyName.trim() && newFamilyImageId;

    const handleDeleteFamily = async () => {
        if (!familyToDelete || !families) return;

        const remainingFamilies = families
            .filter((f) => f.brandRestaurantFamilyId !== familyToDelete.brandRestaurantFamilyId)
            .map((f, index) => ({
                brandRestaurantFamilyId: f.brandRestaurantFamilyId,
                name: f.name,
                imageId: f.imageId,
                position: index,
            }));

        try {
            await setBrandRestaurantFamilies({
                token,
                brandId: parseInt(brandId, 10),
                brandRestaurantFamilies: remainingFamilies,
            }).unwrap();

            setFamilyToDelete(null);
        } catch (error) {
            console.error('Error deleting family:', error);
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination || !families) return;
        if (result.source.index === result.destination.index) return;

        const reorderedFamilies = Array.from(families);
        const [movedFamily] = reorderedFamilies.splice(result.source.index, 1);
        reorderedFamilies.splice(result.destination.index, 0, movedFamily);

        const updatedFamilies = reorderedFamilies.map((family, index) => ({
            brandRestaurantFamilyId: family.brandRestaurantFamilyId,
            name: family.name,
            imageId: family.imageId,
            position: index,
        }));

        try {
            await setBrandRestaurantFamilies({
                token,
                brandId: parseInt(brandId, 10),
                brandRestaurantFamilies: updatedFamilies,
            }).unwrap();
        } catch (error) {
            console.error('Error updating family positions:', error);
        }
    };

    return (
        <>
            <NavigationTabs routes={tabRoutes}>
                <TitleContainer>
                    <Title text="options.main" />
                </TitleContainer>
                <Container>
                    {isLoading ? (
                        <Loader withContainer />
                    ) : (
                        Object.keys(GENERAL_OPTIONS).map((optionName) => (
                            <Item key={optionName} data-testid={`switch-${optionName}`}>
                                <Switch
                                    labelId={GENERAL_OPTIONS[optionName as BrandOptionName]}
                                    onChange={handleChange(optionName as BrandOptionName)}
                                    value={options?.[optionName as BrandOptionName]?.isActivated}
                                    disabled={isSwitchDisabled(optionName as BrandOptionName)}
                                />
                            </Item>
                        ))
                    )}
                </Container>

                <TitleContainer>
                    <Title text="options.foodCourt" />
                </TitleContainer>
                <Container>
                    {isLoading ? (
                        <Loader withContainer />
                    ) : (
                        Object.keys(FOODCOURT_OPTIONS).map((optionName) => (
                            <Item key={optionName} data-testid={`switch-${optionName}`}>
                                <Switch
                                    labelId={FOODCOURT_OPTIONS[optionName as BrandOptionName]}
                                    value={options?.[optionName as BrandOptionName]?.isActivated}
                                    onChange={handleChange(optionName as BrandOptionName)}
                                />
                            </Item>
                        ))
                    )}
                </Container>

                {options?.[BrandOptionName.FOOD_COURT_MODE]?.isActivated && (
                    <>
                        <TitleContainer>
                            <Title text="options.families" />
                        </TitleContainer>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="families" direction="horizontal">
                                {(provided) => (
                                    <FamiliesContainer ref={provided.innerRef} {...provided.droppableProps}>
                                        {isLoadingFamilies ? (
                                            <Loader withContainer />
                                        ) : (
                                            <>
                                                {families?.map((family, index) => (
                                                    <Draggable
                                                        key={family.brandRestaurantFamilyId}
                                                        draggableId={String(family.brandRestaurantFamilyId)}
                                                        index={index}
                                                        isDragDisabled={isSavingFamilies}
                                                    >
                                                        {(providedDrag, snapshotDrag) => (
                                                            <FamilyCard
                                                                ref={providedDrag.innerRef}
                                                                {...providedDrag.draggableProps}
                                                                isDragging={snapshotDrag.isDragging}
                                                            >
                                                                <DragHandle {...providedDrag.dragHandleProps}>
                                                                    <SvyIcon icon="draggable" />
                                                                </DragHandle>
                                                                {imageUrlMap.get(family.imageId) && (
                                                                    <FamilyImage
                                                                        src={imageUrlMap.get(family.imageId)!}
                                                                        alt={family.name}
                                                                    />
                                                                )}
                                                                <FamilyNameContainer>
                                                                    <FamilyName>{family.name}</FamilyName>
                                                                    <DeleteButton
                                                                        onClick={() => setFamilyToDelete(family)}
                                                                        disabled={isSavingFamilies}
                                                                        title={intl.formatMessage({
                                                                            id: 'options.families.delete',
                                                                        })}
                                                                    >
                                                                        <SvyIcon icon="delete-bin-line" />
                                                                    </DeleteButton>
                                                                </FamilyNameContainer>
                                                            </FamilyCard>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                                <NewFamilyForm>
                                                    <SmallImageInput>
                                                        <ImageInput
                                                            brandId={parseInt(brandId, 10)}
                                                            value={newFamilyImageId ?? undefined}
                                                            onChange={(value) => setNewFamilyImageId(value)}
                                                        />
                                                    </SmallImageInput>
                                                    <Input
                                                        value={newFamilyName}
                                                        onChange={(e) => setNewFamilyName(e.target.value)}
                                                        placeholder="options.families.namePlaceholder"
                                                    />
                                                    <Button
                                                        buttonType="primary"
                                                        onClick={handleCreateFamily}
                                                        disabled={!canCreateFamily || isSavingFamilies}
                                                    >
                                                        <FormattedMessage id="options.families.add" />
                                                    </Button>
                                                </NewFamilyForm>
                                            </>
                                        )}
                                    </FamiliesContainer>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </>
                )}

                <TitleContainer>
                    <Title text="options.multiRestaurant" />
                </TitleContainer>
                <Container>
                    {isLoading ? (
                        <Loader withContainer />
                    ) : (
                        <MultiRestaurantsContainer>
                            {Object.keys(MULTI_RESTAURANT_OPTIONS).map((optionName) => (
                                <Item key={optionName} data-testid={`switch-${optionName}`}>
                                    <Switch
                                        labelId={MULTI_RESTAURANT_OPTIONS[optionName as BrandOptionName]}
                                        value={options?.[optionName as BrandOptionName]?.isActivated}
                                        onChange={handleChange(optionName as BrandOptionName)}
                                    />
                                </Item>
                            ))}

                            <MultiRestaurantsDropdownContainer>
                                {options?.[BrandOptionName.MULTI_RESTAURANT_MODE]?.isActivated && (
                                    <Item data-testid="select-main-restaurant">
                                        <SelectContainer>
                                            <Select
                                                options={eligibleRestaurants}
                                                onChange={(value: number) => handleChangeMainRestaurant(value)}
                                                value={mainRestaurantId}
                                                placeholder={
                                                    eligibleRestaurants.length > 0 ? eligibleRestaurants[0].label : ''
                                                }
                                                labelId="options.multiRestaurantModeMainRestaurant"
                                            />
                                        </SelectContainer>
                                    </Item>
                                )}

                                {options?.[BrandOptionName.MULTI_RESTAURANT_MODE]?.isActivated && (
                                    <Item data-testid="select-main-user">
                                        <SelectContainer>
                                            <Select
                                                options={eligibleUsers}
                                                onChange={(value: number) => handleChangeMainUser(value)}
                                                value={mainUserId}
                                                placeholder={'----------------------'}
                                                labelId="options.multiRestaurantModeMainUser"
                                            />
                                        </SelectContainer>
                                    </Item>
                                )}

                                {options?.[BrandOptionName.MULTI_RESTAURANT_MODE]?.isActivated && (
                                    <Item data-testid="select-assigned-restaurants">
                                        <SelectContainer>
                                            <MultiSelect
                                                isMulti={true}
                                                options={assignableRestaurants}
                                                onChange={handleChangeAssignedRestaurants}
                                                placeholder={''}
                                                value={assignedRestaurants}
                                                labelId="options.multiRestaurantModeAssignedRestaurants"
                                                customStyle={{
                                                    multiValue: {},
                                                    multiValueLabel: {},
                                                    valueContainer: {
                                                        maxHeight: 'none',
                                                        overflow: 'visible',
                                                    },
                                                    control: {
                                                        minHeight: 'auto',
                                                        minWidth: '400px',
                                                    },
                                                    indicatorsContainer: {
                                                        alignSelf: 'flex-start',
                                                        paddingTop: '4px',
                                                    },
                                                }}
                                            />
                                        </SelectContainer>
                                    </Item>
                                )}
                            </MultiRestaurantsDropdownContainer>
                        </MultiRestaurantsContainer>
                    )}
                </Container>
            </NavigationTabs>
            <SavingBar
                onSave={handleSave}
                changesCount={changeCount}
                onCancel={handleCancel}
                loading={isLoadingUpdate || isLoadingUpdateModuleOrderingFlow || isLoadingUpdateUserRestaurants}
            />
            <Modal
                isOpen={!!familyToDelete}
                onHide={() => setFamilyToDelete(null)}
                title={intl.formatMessage({ id: 'options.families.deleteTitle' }, { name: familyToDelete?.name ?? '' })}
                buttons={
                    <Button buttonType="primary" onClick={handleDeleteFamily} disabled={isSavingFamilies}>
                        <FormattedMessage id="button.confirm" />
                    </Button>
                }
            >
                <FormattedMessage id="options.families.deleteConfirmation" values={{ name: familyToDelete?.name }} />
            </Modal>
        </>
    );
};
